import type { Execution } from '../api/generated';
import { describePhase, describeStatus, hasBuildOutput, isFinished } from './executionText';
import { TerminalDrawer, type TerminalTab } from './TerminalDrawer';
import { TerminalOutput } from './TerminalOutput';

export interface PasteTerminalProps {
  readonly stdin: string;
  readonly execution: Execution | undefined;
  /** Why there is no output yet, when there is no execution. */
  readonly idleMessage: string;
  readonly activeTab: string;
  readonly onSelect: (tab: string) => void;
}

/** A paste's stdin and the stdout, stderr and build output of its execution. */
export function PasteTerminal({ stdin, execution, idleMessage, activeTab, onSelect }: PasteTerminalProps) {
  const run = execution?.run;
  const inProgress = execution !== undefined && !isFinished(execution);
  const pending = execution === undefined ? idleMessage : execution.status === 'build_failed' ? 'The build failed: see the build tab.' : describeStatus(execution);

  const tabs: TerminalTab[] = [
    {
      id: 'stdin',
      label: 'stdin',
      content: <TerminalOutput label="Standard input" output={{ text: stdin, truncated: false }} placeholder="(no input)" />,
    },
    {
      id: 'stdout',
      label: 'stdout',
      ...(inProgress ? { badge: 'busy' as const } : {}),
      content:
        run === undefined ? (
          <TerminalOutput label="Standard output" placeholder={pending} />
        ) : (
          <TerminalOutput label="Standard output" output={run.stdout} footer={[describePhase(run)]} />
        ),
    },
    {
      id: 'stderr',
      label: 'stderr',
      content:
        run === undefined ? (
          <TerminalOutput label="Standard error" placeholder={pending} />
        ) : (
          <TerminalOutput label="Standard error" output={run.stderr} placeholder="(nothing on stderr)" />
        ),
    },
  ];
  const build = execution?.build;
  if (build !== undefined && hasBuildOutput(execution)) {
    const failed = execution?.status === 'build_failed';
    tabs.push({
      id: 'build',
      label: 'build',
      ...(failed ? { badge: 'error' as const } : {}),
      content: (
        <TerminalOutput
          label="Build output"
          output={{ text: build.stdout.text + build.stderr.text, truncated: build.stdout.truncated || build.stderr.truncated }}
          footer={[`build ${failed ? 'failed: ' : ''}${describePhase(build)}`]}
        />
      ),
    });
  }
  return <TerminalDrawer tabs={tabs} activeTab={activeTab} onSelect={onSelect} />;
}
